import { useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { ESlotStatus, StreamingAccount } from "@/services/streaming-account";

interface AccountComboboxProps {
  accounts: StreamingAccount[];
  value: number | undefined;
  onChange: (accountId: number | undefined) => void;
  disabled?: boolean;
  size?: "sm" | "default";
}

export function AccountCombobox({
  accounts,
  value,
  onChange,
  disabled,
  size = "default",
}: AccountComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = search
    ? accounts.filter((a) =>
        a.email.toLowerCase().includes(search.toLowerCase())
      )
    : accounts;

  const selected = accounts.find((a) => a.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            size === "sm" ? "h-8 text-xs" : "h-9"
          )}
          disabled={disabled || accounts.length === 0}
        >
          {selected ? (
            <span className="truncate">{selected.email}</span>
          ) : (
            <span className="text-muted-foreground">
              {accounts.length === 0 ? "Sin cuentas activas" : "Buscar cuenta..."}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por email..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {filtered.length === 0 ? (
              <CommandEmpty>
                {search ? "No se encontraron cuentas" : "Sin cuentas activas"}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {filtered.map((acc) => {
                  const avail =
                    acc.slots?.filter((s) => s.status === ESlotStatus.AVAILABLE)
                      .length ?? 0;
                  return (
                    <CommandItem
                      key={acc.id}
                      value={acc.id.toString()}
                      onSelect={() => {
                        onChange(acc.id === value ? undefined : acc.id);
                        setOpen(false);
                        setSearch("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === acc.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span className={cn(size === "sm" ? "text-xs" : "text-sm")}>
                          {acc.email}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {avail} slot(s) disponible(s)
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
